import React, { memo, useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Input } from "@components/atoms";
import { useTranslation } from "react-i18next";
import CustomSelect from "@components/atoms/CustomSelect/CustomSelect";
import { ButtonCategory } from "@components/atoms/Button/types";
import styled from "styled-components/native";
import { FormType, RowType } from "@components/molecules/DynamicForm/types";
import CountryCodePicker from "@components/atoms/CountryPicker";
import CrossPlatformCalendar from "@components/molecules/DynamicForm/components/CrossPlatformCalendar";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import {
  generateHoldOptions,
  generateYupSchema,
  getInputNames,
  hasFancyItem,
  hasNullDiscountItem,
  onBlurChange,
  onChangeText,
  onParcelBlurChange,
  onParcelChangeText,
} from "./utils";
import CustomSelectModal from "../customSelectModal/customSelectModal";
import { useDynamicFormState } from "@scenes/bulk-request/context/DynamicFormContext";
import RadioSelect from "./components/RadioSelect";
import { useTheme } from "styled-components/native";
import CountryCodeDropdownPicker from "@components/atoms/CountryCodePickerWeb";
import useBulkInitScreenModals from "../BulkListItemComponents/useBulkInitScreenModal";
import { removeCommaFormatted } from "@components/atoms/Filters/FilterSection/Utils";
import ParcelPriceSectionContainer from "../ParcelPriceContainerSection";
import { View } from "react-native";
import DropdownWithPortal from "../BMH/components/WebHoursSlideSheet";
import { addComma } from "@utils/product/productFormatters";
import {
  addPercentage,
  calculateItemPrices,
  formatNumberWithPrecision,
} from "@utils/product/productFormatters";
import { isMeleeItem } from "@utils/product/productValidators";
import { useAppSettings } from "@redux/slices/globalSlice";

const DynamicForm = ({
  form,
  hideSubmitButton,
  minDate,
  itemDetail = {} as any,
  itemList = [],
  setItemList,
  stockItemRequest,
  fromRequestModify = false,
  skipHasNullDiscount,
  testIDPrefix = "dynamicForm",
}: FormType) => {
  const requiredSymbol = "*";
  const { t } = useTranslation();
  const theme = useTheme();
  const hasFancy = hasFancyItem(itemList, itemDetail);
  const hasNullDiscount = hasNullDiscountItem(itemList, itemDetail);
  const [countryCode, setCountyCode] = useState("");
  const userInfo = useUserInfo();
  const maxHoldHours = userInfo.user.feature_settings.max_hold_hours;
  let disableDiscountFiled = skipHasNullDiscount && hasNullDiscount;
  const getAppSettings = useAppSettings();
  const enableFourDecimalCarat =
    getAppSettings?.enable_four_decimal_carat ?? false;

  const price_per_carat =
    formatNumberWithPrecision((itemDetail as any)?.updated_price_per_carat, {
      precision: 0,
    }) ||
    formatNumberWithPrecision((itemDetail as any)?.price_per_carat, {
      precision: 0,
    });
  const total_sales_price =
    formatNumberWithPrecision((itemDetail as any)?.updated_total_sales_price, {
      precision: 2,
    }) ||
    formatNumberWithPrecision(itemDetail?.total_sales_price, {
      precision: 2,
    });
  const total_discount_percent =
    (itemDetail as any)?.updated_discount_percent ||
    itemDetail?.discount_percent ||
    stockItemRequest?.offer_discount_percent;
  const defaultDynamicInput = form?.filter((field) => {
    if (field?.type === "radioSelect") {
      return field;
    }
  });
  const [dynamicInput, setDynamicInput] = useState(
    defaultDynamicInput?.[0]?.sub_section?.[0]?.nestedInput || ""
  );

  const holdOptions = generateHoldOptions(maxHoldHours, t);
  const pickerItems = maxHoldHours.map((h) => {
    const num = Number(h);
    return num > 72
      ? `${num / 24} ${t("bmh.hold.hour.type.days.text")}`
      : `${num} ${t("bmh.hold.hour.type.hour.text")}`;
  });
  const filterDynamicInputs = () => {
    return form?.filter((field) => {
      if (
        (field?.name === "offerDiscountPercent" ||
          field.name === "totalOfferDiscount") &&
        hasFancyItem(itemList)
      ) {
        return false;
      }

      if (field?.showHide) {
        return dynamicInput?.includes(field.name);
      }
      return true;
    });
  };

  const hasTotalOfferPrice = () => {
    return itemList.some((obj) => {
      return !!obj?.item?.updated_total_sales_price;
    });
  };

  const [schema, setSchema] = useState(() =>
    generateYupSchema(filterDynamicInputs(), t)
  );
  const {
    setDynamicFormValues,
    setSingleFormValues,
    dynamicFormValues,
    resetSingleFormValues,
    setRequestFormValue,
    resetRequestFormValue,
    setSingleRequestFormValue,
    setIsDirty,
    setDynamicFormValidations,
  } = useDynamicFormState();
  const newTotalOfferPrice =
    Object.keys(itemList).length > 0 && !stockItemRequest
      ? Object.values(itemList).reduce(
          (sum, item: any) =>
            sum +
            (Number(
              item?.item?.updated_total_sales_price ||
                item?.item?.total_sales_price
            ) || 0),
          0
        )
      : fromRequestModify
      ? Number(stockItemRequest?.offer_total_sales_price) !==
        Object.values(itemList).reduce(
          (sum, item: any) =>
            sum +
            Number(item?.updated_total_sales_price || item?.total_sales_price),
          0
        )
        ? itemList.some(
            (item) =>
              item.updated_total_sales_price !== undefined &&
              item.updated_total_sales_price !== ""
          )
          ? Object.values(itemList).reduce(
              (sum, item: any) =>
                sum +
                Number(
                  item?.updated_total_sales_price || item?.total_sales_price
                ),
              0
            )
          : Number(stockItemRequest?.offer_total_sales_price)
        : Object.values(itemList).reduce(
            (sum, item: any) =>
              sum +
              Number(
                item?.updated_total_sales_price || item?.total_sales_price
              ),
            0
          )
      : Object.values(itemList).reduce(
          (sum, item: any) =>
            sum +
            Number(item?.updated_total_sales_price || item?.total_sales_price),
          0
        ) || Number(stockItemRequest?.offer_total_sales_price);
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    setError,
    resetField,
    formState: { errors, isValid, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: filterDynamicInputs().reduce((acc: any, field: any) => {
      switch (field.name) {
        case "totalOfferPrice": {
          const offerTotalSalesPrice =
            stockItemRequest?.offer_total_sales_price;
          const totalOfferSource =
            fromRequestModify &&
            offerTotalSalesPrice !== undefined &&
            offerTotalSalesPrice !== null &&
            offerTotalSalesPrice !== ""
              ? offerTotalSalesPrice
              : total_sales_price;
          acc[field.name] =
            `${addComma(
              totalOfferSource?.toString()?.replaceAll(",", "") || ""
            )}` || "0.00";
          break;
        }
        case "pricePerOffer":
          acc[field.name] =
            `${addComma(
              price_per_carat?.toString()?.replaceAll(",", "") || ""
            )}` || "0.00";
          break;
        case "totalOfferDiscount":
          acc[field.name] = addPercentage(total_discount_percent) || "-";
          break;
        case "comments":
          acc[field.name] = fromRequestModify
            ? stockItemRequest?.comments
              ? stockItemRequest?.comments
              : (itemDetail as any)?.updated_comments ||
                itemDetail?.comments ||
                field.defaultValue ||
                ""
            : (itemDetail as any)?.updated_comments ||
              field.defaultValue ||
              stockItemRequest?.comments ||
              "";
          break;
        case "hold_hours":
          acc[field.name] =
            stockItemRequest?.hold_hours || holdOptions[0].value || "";
          break;
        case "offerTotalPrice":
          if (fromRequestModify) {
            acc[field.name] =
              `${addComma(
                stockItemRequest?.offer_total_sales_price
                  ?.toString()
                  ?.replaceAll(",", "")
              )}` || "0.00";
          } else {
            acc[field.name] =
              `${addComma(
                (
                  formatNumberWithPrecision(newTotalOfferPrice, {
                    precision:
                      userInfo?.roundUpPrices() && !isMeleeItem(itemDetail)
                        ? 0
                        : 2,
                  }) ?? "0.00"
                ).replaceAll(",", "")
              )}` || "0.00";
          }
          break;
        case "offerTotalDiscount":
          if (fromRequestModify) {
            acc[field.name] = addPercentage(total_discount_percent) || "";
          } else {
            acc[field.name] = "";
          }
          break;
        case "radioSelect":
          acc[field.name] =
            stockItemRequest?.payment_type?.toLowerCase() ?? field.defaultValue;
          break;
        case "requestedQuantity":
          acc[field.name] = fromRequestModify
            ? itemDetail?.quantity
              ? itemDetail?.quantity
              : ""
            : "";
          break;
        case "requestedTotalCarat":
          acc[field.name] = fromRequestModify
            ? itemDetail?.offer_size
              ? itemDetail?.offer_size.toString() + " ct"
              : ""
            : "";
          break;
        default:
          acc[field.name] = field.defaultValue || "";
      }
      return acc;
    }, {}),
  });
  const memoizedGetValues = useCallback(() => getValues(), [getValues]);
  const { renderModals, showModal } = useBulkInitScreenModals(
    dynamicFormValues,
    setValue
  );

  useEffect(() => {
    setDynamicFormValidations?.(
      Object.keys(itemDetail).length > 0 ? "singleForm" : "requestForm",
      {
        control,
        handleSubmit,
        getValues,
        isValid,
        isDirty,
        errors,
      }
    );
  }, [control, handleSubmit, memoizedGetValues, isValid, isDirty, errors]);
  const values = getValues();
  const onSubmit = (data: any) => {
    console.log(data);
  };

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty]);

  const onValueChange = (val: string, onChange: any) => {
    setCountyCode(val);
    onChange(val);
    const updatedFormData = getValues();
    if (Object.keys(itemDetail).length > 0) {
      setSingleFormValues(itemDetail?.id as string, updatedFormData);
    } else {
      setDynamicFormValues("requestForm", updatedFormData);
    }
  };
  const handleNestedInputChange = (nestedInput: any) => {
    setDynamicInput(nestedInput);
  };
  useEffect(() => {
    if (Object.keys(itemDetail).length > 0) {
      setSingleFormValues(itemDetail?.id as string, values);
    } else {
      setDynamicFormValues("requestForm", values);
    }
  }, []);

  useEffect(() => {
    if (
      newTotalOfferPrice &&
      (Object.keys(dynamicFormValues.singleForm).length > 0 ||
        dynamicFormValues.requestForm.offerTotalDiscount)
    ) {
      setDynamicFormValues(
        "totalOfferPrice",
        formatNumberWithPrecision(newTotalOfferPrice, {
          precision:
            userInfo?.roundUpPrices() && !isMeleeItem(itemDetail) ? 0 : 2,
        })?.replaceAll(",", "") || dynamicFormValues?.totalOfferPrice
      );
    }
    if (dynamicFormValues.requestForm.offerTotalDiscount === "") {
      setRequestFormValue("offerTotalDiscount", "");
      setValue("offerTotalDiscount", "");
    }
  }, [newTotalOfferPrice]);

  useEffect(() => {
    if (dynamicFormValues?.totalOfferPrice) {
      setValue(
        "offerTotalPrice",
        `${addComma(
          dynamicFormValues?.totalOfferPrice?.toString()?.replaceAll(",", "")
        )}` || "0.00"
      );
    }
  }, [dynamicFormValues?.totalOfferPrice]);

  useEffect(() => {
    setSchema(generateYupSchema(filterDynamicInputs(), t));
    resetField("payWithin", {
      defaultValue:
        stockItemRequest?.pay_within ||
        dynamicFormValues?.requestForm?.payWithin ||
        "30",
    });
  }, [dynamicInput]);

  useEffect(() => {
    if (dynamicFormValues.requestForm.offerTotalDiscount) {
      if (!itemList?.length) return;

      const newItemList = itemList.map((item) => {
        const dataItem = stockItemRequest ? item : item.item;
        if (!dataItem) return item;

        const calculatedPrice = calculateItemPrices(
          dataItem,
          "discount",
          dynamicFormValues.requestForm.offerTotalDiscount
        );

        const formattedTotalPrice = calculatedPrice.totalPrice
          ? userInfo?.roundUpPrices() && !isMeleeItem(dataItem)
            ? formatNumberWithPrecision(calculatedPrice.totalPrice, {
                precision: 0,
              })
            : formatNumberWithPrecision(calculatedPrice.totalPrice, {
                precision: 2,
              })
          : "";
        const formattedPpc = calculatedPrice.ppc
          ? userInfo?.roundUpPrices() && !isMeleeItem(dataItem)
            ? formatNumberWithPrecision(calculatedPrice.ppc, { precision: 0 })
            : formatNumberWithPrecision(calculatedPrice.ppc, { precision: 2 })
          : "";

        if (stockItemRequest) {
          return {
            ...item,
            updated_total_sales_price: formattedTotalPrice,
            updated_price_per_carat: formattedPpc,
            updated_discount_percent:
              dynamicFormValues.requestForm.offerTotalDiscount,
          };
        } else {
          return {
            ...item,
            item: {
              ...item.item,
              updated_total_sales_price: formattedTotalPrice,
              updated_price_per_carat: formattedPpc,
              updated_discount_percent:
                dynamicFormValues.requestForm.offerTotalDiscount,
            },
          };
        }
      });
      setItemList(newItemList);
    }
  }, [dynamicFormValues.requestForm.offerTotalDiscount]);
  const renderField = (field: RowType) => {
    let resolvedCountryCode = "";
    if (field.type === "countryCode") {
      resolvedCountryCode = dynamicFormValues?.requestForm?.countryCode
        ? dynamicFormValues?.requestForm?.countryCode
        : field.defaultValue;
      if (resolvedCountryCode) {
        setValue("countryCode", resolvedCountryCode);
      }
    }

    const commonProps = {
      control,
      name: field.name,
      render: ({
        field: { onChange, onBlur, value },
      }: {
        field: {
          onChange: (val: string) => void;
          onBlur: () => void;
          value: string;
        };
      }) => {
        const label = `${t(field.label)}${
          field?.validations?.required ? requiredSymbol : ""
        }`;
        const error = errors?.[field.name]?.message as string;
        const controlTestId = (suffix: string) =>
          `${testIDPrefix}.field.${field.name}.${suffix}`;
        switch (field.type) {
          case "dropdown":
            return (
              <View
                style={{ width: "100%" }}
                testID={controlTestId("dropdown")}
              >
                <CustomSelect
                  key={field.name}
                  title={`${t(field.label)}${
                    field?.validations?.required ? requiredSymbol : ""
                  }`}
                  defaultValue={
                    field.name === "hold_hours"
                      ? stockItemRequest?.hold_hours?.toLowerCase() ||
                        holdOptions[0].value
                      : value
                  }
                  options={
                    field.name === "hold_hours"
                      ? holdOptions
                      : field.options || []
                  }
                  onSelect={(_value: string) => onValueChange(_value, onChange)}
                  field={field}
                  setSingleRequestFormValue={setRequestFormValue}
                  setFormValue={setValue}
                  inputNames={getInputNames(filterDynamicInputs())}
                  error={error}
                  //@ts-ignore
                  showModal={showModal}
                  productType={itemDetail?.type}
                  dynamicFormValues={dynamicFormValues}
                />
              </View>
            );
          case "modalSelect":
            return field.name === "hold_hours" && theme.isLargeScreen ? (
              <View
                style={{ zIndex: 10000 }}
                testID={controlTestId("modalSelect")}
              >
                <DropdownWithPortal
                  items={pickerItems}
                  selected={dynamicFormValues?.requestForm?.hold_hours || ""}
                  onSelect={(val) => onValueChange(val, onChange)}
                  placeholder="Select hold time"
                  screenText={field?.screenText ?? ""}
                />
              </View>
            ) : (
              <View
                style={{ width: "100%" }}
                testID={controlTestId("modalSelect")}
              >
                <CustomSelectModal
                  title={label}
                  defaultValue={
                    field.name === "hold_hours"
                      ? stockItemRequest?.hold_hours?.toLowerCase() ||
                        holdOptions[0].value
                      : value
                  }
                  options={
                    field.name === "hold_hours"
                      ? holdOptions
                      : field.options || []
                  }
                  onSelect={(val) => onValueChange(val, onChange)}
                  modalTitle={field?.modalTitle}
                  screenText={field?.screenText}
                />
              </View>
            );

          case "date":
            return (
              <View style={{ width: "100%" }} testID={controlTestId("date")}>
                <CrossPlatformCalendar
                  label={label}
                  value={value}
                  onChange={(val) => onValueChange(val, onChange)}
                  required={field?.validations?.required ?? false}
                  error={error}
                  minDate={minDate || undefined}
                />
              </View>
            );

          case "textarea":
            return (
              <Input
                testID={controlTestId("textarea")}
                inputLabel={label}
                onChangeText={(val) => onValueChange(val, onChange)}
                value={value}
                multiline
                height="96"
                error={error}
              />
            );

          case "countryCode":
            return theme.isLargeScreen ? (
              <View
                style={{ width: "100%" }}
                testID={controlTestId("countryCode")}
              >
                <CountryCodeDropdownPicker
                  selected={countryCode}
                  setSelected={(val) => onValueChange(val, onChange)}
                  setCountryDetails={() => {}}
                  isGuest={userInfo.isGuest}
                  fromBulkList={true}
                  defaultValue={
                    dynamicFormValues?.requestForm?.countryCode
                      ? dynamicFormValues?.requestForm?.countryCode
                      : field.defaultValue
                  }
                />
              </View>
            ) : (
              <View
                style={{ width: "100%" }}
                testID={controlTestId("countryCode")}
              >
                <CountryCodePicker
                  onCountrySelect={(code: any) =>
                    onValueChange(code[0], onChange)
                  }
                  isGuest={userInfo.isGuest}
                  fromBulkList={true}
                  selected={resolvedCountryCode}
                />
              </View>
            );

          case "priceInput":
          case "offerTotalDiscount":
          case "offerTotalPrice":
            return (
              <Input
                testID={controlTestId("input")}
                inputLabel={label}
                keyboardType="numeric"
                currency={
                  field.name === "pricePerOffer" ||
                  field.name === "totalOfferPrice" ||
                  field.name === "offerTotalPrice"
                    ? true
                    : false
                }
                onBlur={() => {
                  if (
                    Number(removeCommaFormatted(value)) < 1 &&
                    (field.name === "pricePerOffer" ||
                      field.name === "totalOfferPrice" ||
                      field.name === "offerTotalPrice")
                  ) {
                    setError(field.name, {
                      type: "manual",
                      message: field.validations?.errorMessage
                        ? t(`${field.validations?.errorMessage}`)
                        : "Please enter a Value greater then 0",
                    });
                  } else {
                    onBlurChange(
                      field.name,
                      onBlur,
                      value,
                      getValues,
                      setValue,
                      userInfo,
                      setSingleFormValues,
                      //@ts-ignore
                      setDynamicFormValues,
                      values,
                      itemDetail?.id,
                      (field.type === "offerTotalPrice" ||
                        field.type === "offerTotalDiscount") &&
                        (field.type === "offerTotalDiscount" ||
                          fromRequestModify)
                        ? hasTotalOfferPrice() ||
                            formatNumberWithPrecision(newTotalOfferPrice, {
                              precision:
                                userInfo?.roundUpPrices() &&
                                !isMeleeItem(itemDetail)
                                  ? 0
                                  : 2,
                            })?.replaceAll(",", "") !==
                              dynamicFormValues.requestForm.offerTotalPrice
                                ?.toString()
                                ?.replaceAll(",", "")
                        : hasTotalOfferPrice(),
                      showModal,
                      resetSingleFormValues,
                      setRequestFormValue,
                      itemDetail
                    );
                  }
                }}
                onChangeText={(val) => {
                  onChangeText(
                    field.name,
                    val,
                    onChange,
                    setSingleFormValues,
                    //@ts-ignore
                    setDynamicFormValues,
                    values,
                    itemDetail,
                    userInfo,
                    setValue,
                    setError,
                    disableDiscountFiled
                  );
                }}
                {...(field.name === "offerTotalDiscount" && {
                  onFocus: () => {
                    if (!value) {
                      setValue("offerTotalDiscount", "-");
                    }
                  },
                })}
                value={value}
                error={error}
                disabled={
                  disableDiscountFiled &&
                  (field.name === "offerTotalDiscount" ||
                    field.name === "totalOfferDiscount")
                }
              />
            );

          case "quantityInput":
          case "caratInput":
            return (
              <Input
                testID={controlTestId("input")}
                inputLabel={label}
                keyboardType="numeric"
                onBlur={() => {
                  onParcelBlurChange(
                    field.name,
                    onBlur,
                    value,
                    setValue,
                    setDynamicFormValues,
                    values,
                    itemDetail?.id,
                    enableFourDecimalCarat
                  );
                }}
                onChangeText={(val) => {
                  onParcelChangeText(
                    field.name,
                    val,
                    onChange,
                    setSingleFormValues,
                    values,
                    itemDetail,
                    setValue,
                    setError,
                    enableFourDecimalCarat
                  );
                }}
                onFocus={() => {
                  if (
                    field.name === "requestedTotalCarat" &&
                    value?.endsWith(" ct")
                  ) {
                    setValue(field.name, value.replace(" ct", ""));
                  }
                }}
                value={value}
                error={error}
              />
            );

          case "parcelPriceSection":
            return (
              <View
                style={{ width: "100%" }}
                testID={controlTestId("parcelPrice")}
              >
                <ParcelPriceSectionContainer
                  itemDetail={itemDetail}
                  totalCaratValue={values.requestedTotalCarat}
                  translate={t}
                />
              </View>
            );

          case "radioSelect":
            return (
              <View
                style={{ width: "100%" }}
                testID={controlTestId("radioSelect")}
              >
                <RadioSelect
                  productType={itemDetail?.type}
                  options={field?.sub_section}
                  onOptionChange={handleNestedInputChange}
                  values={values}
                  setValue={setValue}
                  setRequestFormValue={setRequestFormValue}
                  setSingleRequestFormValue={setSingleRequestFormValue}
                  itemDetail={itemDetail}
                  selectedValue={value}
                  resetRequestFormValue={resetRequestFormValue}
                  formInputs={filterDynamicInputs()}
                />
              </View>
            );

          default:
            return (
              <Input
                testID={controlTestId("input")}
                inputLabel={label}
                onChangeText={(val) => onValueChange(val, onChange)}
                value={value}
                error={error}
              />
            );
        }
      },
    };

    return <Controller {...commonProps} />;
  };

  return (
    <>
      {renderModals()}
      <RowsContainer>
        {form?.map((field: any, index: number) => {
          if (field?.showHide && !dynamicInput?.includes(field?.name)) {
            return null;
          }
          if (
            (hasFancy || (skipHasNullDiscount ? false : hasNullDiscount)) &&
            (field.name === "offerTotalDiscount" ||
              field.name === "totalOfferDiscount")
          ) {
            return null;
          }
          return (
            <React.Fragment key={field.name || `field-${index}`}>
              <InputContainer
                testID={`${testIDPrefix}.field.${field.name}`}
                fieldWidth={theme.isMobile ? field?.mobileWidth : field?.width}
                index={index}
                formLength={form.length}
              >
                {renderField(field)}
              </InputContainer>
              {field?.padding && <Space padding={field?.padding} />}
            </React.Fragment>
          );
        })}
      </RowsContainer>
      {!hideSubmitButton && (
        <Button
          onPress={handleSubmit(onSubmit)}
          category={ButtonCategory.default}
          title={"Submit"}
          testID={`${testIDPrefix}.submit`}
        />
      )}
    </>
  );
};

const Space = styled.View<{ padding: string }>`
  width: ${({ padding }: { padding: string }) => padding};
`;

const RowsContainer = styled.View`
  width: 100%;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const InputContainer = styled.View<{
  fieldWidth?: string;
  index: number;
  formLength: number;
}>`
  width: ${({ fieldWidth }: { fieldWidth?: string }) => fieldWidth || "100%"};
  margin-bottom: ${({
    index,
    formLength,
  }: {
    index: number;
    formLength: number;
  }) => (index === formLength - 1 ? "0" : SpaceType.Double)}px;
`;

export default memo(DynamicForm);
